import React, { useState, useEffect, useCallback } from 'react';
import {
  getPageContainerStyles,
  getSearchFormStyles,
  getTableStyles,
  getStatusFilterStyles,
  getLoadingStyles,
  getStatusStyles,
  getModalStyles,
  getModalContentStyles,
  getModalActionStyles,
  getDateTagStyles,
  getMessageModalStyles,
  getDateTimeInputStyles,
  getCommonInputStyles
} from '../utils/styleUtils';
import { useLanguage } from '../utils/language';
import styles from '../styles/StyleGuidePage.module.css';

const StyleGuidePage = () => {
  const lang = useLanguage();
  const [activeTab, setActiveTab] = useState('pageLayout');
  const [showModal, setShowModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [lineWidth, setLineWidth] = useState(50);

  // 모달이 열려있을 때 백그라운드 스크롤 방지
  useEffect(() => {
    if (showModal || showMessageModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showMessageModal]);

  // 동적으로 직선 길이 계산
  const calculateLineWidths = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const tabContainer = document.querySelector('.styleguide-tab-container');

    if (tabContainer) {
      const tabRect = tabContainer.getBoundingClientRect();
      const leftStart = tabRect.left; // 탭 컨테이너의 왼쪽 위치
      const rightEnd = viewportWidth - tabRect.right; // 뷰포트 끝에서 탭 컨테이너 오른쪽까지의 거리

      setLineWidth(Math.max(leftStart, rightEnd)); // 더 긴 쪽을 사용
    }
  }, []);

  // 컴포넌트 마운트 시와 윈도우 리사이즈 시 계산
  useEffect(() => {
    // DOM이 렌더링된 후 직선 길이 계산
    setTimeout(() => {
      calculateLineWidths();
    }, 100);

    window.addEventListener('resize', calculateLineWidths);
    return () => window.removeEventListener('resize', calculateLineWidths);
  }, [calculateLineWidths]);

  // 스타일 유틸리티 가져오기
  const pageContainerStyles = getPageContainerStyles();
  const searchStyles = getSearchFormStyles();
  const tableStyles = getTableStyles();
  const statusFilterStyles = getStatusFilterStyles();
  const loadingStyles = getLoadingStyles();
  const statusStyles = getStatusStyles();
  const modalStyles = getModalStyles();
  const modalContentStyles = getModalContentStyles();
  const modalActionStyles = getModalActionStyles();
  // tabStyles와 filterStyles는 현재 사용되지 않음
  const dateTagStyles = getDateTagStyles();
  const messageModalStyles = getMessageModalStyles();
  const dateTimeInputStyles = getDateTimeInputStyles();
  const commonInputStyles = getCommonInputStyles();

  const TEXT = {
    pageTitle: {
      ko: '스타일 가이드',
      en: 'Style Guide',
      fr: 'Guide de style'
    },
    buttons: {
      ko: '버튼',
      en: 'Buttons',
      fr: 'Boutons'
    },
    forms: {
      ko: '폼',
      en: 'Forms',
      fr: 'Formulaires'
    },
    tables: {
      ko: '테이블',
      en: 'Tables',
      fr: 'Tableaux'
    },
    modals: {
      ko: '모달',
      en: 'Modals',
      fr: 'Modales'
    },
    status: {
      ko: '상태',
      en: 'Status',
      fr: 'Statut'
    },
    tabs: {
      ko: '탭',
      en: 'Tabs',
      fr: 'Onglets'
    },
    search: {
      ko: '검색',
      en: 'Search',
      fr: 'Recherche'
    },
    filters: {
      ko: '필터',
      en: 'Filters',
      fr: 'Filtres'
    },
    inputs: {
      ko: '입력',
      en: 'Inputs',
      fr: 'Entrées'
    },
    dateTags: {
      ko: '날짜 태그',
      en: 'Date Tags',
      fr: 'Tags de date'
    },
    tab1: {
      ko: '탭1',
      en: 'Tab1',
      fr: 'Onglet1'
    },
    tab2: {
      ko: '탭2',
      en: 'Tab2',
      fr: 'Onglet2'
    },
    tab3: {
      ko: '탭3',
      en: 'Tab3',
      fr: 'Onglet3'
    },
    tab4: {
      ko: '탭4',
      en: 'Tab4',
      fr: 'Onglet4'
    },
    loading: {
      ko: '로딩',
      en: 'Loading',
      fr: 'Chargement'
    },
    sampleText: {
      ko: '샘플 텍스트',
      en: 'Sample Text',
      fr: 'Texte d\'exemple'
    },
    primary: {
      ko: '주요',
      en: 'Primary',
      fr: 'Principal'
    },
    secondary: {
      ko: '보조',
      en: 'Secondary',
      fr: 'Secondaire'
    },
    delete: {
      ko: '삭제',
      en: 'Delete',
      fr: 'Supprimer'
    },
    confirm: {
      ko: '확정',
      en: 'Confirm',
      fr: 'Confirmer'
    },
    pending: {
      ko: '대기중',
      en: 'Pending',
      fr: 'En attente'
    },
    confirmed: {
      ko: '확정됨',
      en: 'Confirmed',
      fr: 'Confirmé'
    },
    cancelled: {
      ko: '취소됨',
      en: 'Cancelled',
      fr: 'Annulé'
    },
    completed: {
      ko: '완료됨',
      en: 'Completed',
      fr: 'Terminé'
    },
    depositConfirmed: {
      ko: '선금확인',
      en: 'Deposit Confirmed',
      fr: 'Acompte confirmé'
    },
    all: {
      ko: '전체',
      en: 'All',
      fr: 'Tous'
    },
    openModal: {
      ko: '모달 열기',
      en: 'Open Modal',
      fr: 'Ouvrir modal'
    },
    openMessageModal: {
      ko: '메시지 모달 열기',
      en: 'Open Message Modal',
      fr: 'Ouvrir modal message'
    },
    close: {
      ko: '닫기',
      en: 'Close',
      fr: 'Fermer'
    },
    save: {
      ko: '저장',
      en: 'Save',
      fr: 'Enregistrer'
    },
    update: {
      ko: '수정',
      en: 'Update',
      fr: 'Modifier'
    },
    searchPlaceholder: {
      ko: '검색어를 입력하세요...',
      en: 'Enter search term...',
      fr: 'Entrez votre recherche...'
    },
    name: {
      ko: '이름',
      en: 'Name',
      fr: 'Nom'
    },
    email: {
      ko: '이메일',
      en: 'Email',
      fr: 'Email'
    },
    phone: {
      ko: '전화번호',
      en: 'Phone',
      fr: 'Téléphone'
    },
    statusHeader: {
      ko: '상태',
      en: 'Status',
      fr: 'Statut'
    },
    date: {
      ko: '날짜',
      en: 'Date',
      fr: 'Date'
    },
    description: {
      ko: '설명',
      en: 'Description',
      fr: 'Description'
    },
    sampleData: {
      ko: '샘플 데이터',
      en: 'Sample Data',
      fr: 'Données d\'exemple'
    },
    noData: {
      ko: '데이터가 없습니다',
      en: 'No data',
      fr: 'Aucune donnée'
    },
    loadingText: {
      ko: '로딩 중...',
      en: 'Loading...',
      fr: 'Chargement...'
    }
  };

  const tabs = [
    { id: 'pageLayout', label: '페이지 레이아웃' },
    { id: 'text', label: '텍스트' },
    { id: 'buttons', label: TEXT.buttons[lang] },
    { id: 'forms', label: TEXT.forms[lang] },
    { id: 'checkboxes', label: '체크박스' },
    { id: 'radios', label: '라디오박스' },
    { id: 'tables', label: TEXT.tables[lang] },
    { id: 'modals', label: TEXT.modals[lang] },
    { id: 'status', label: TEXT.status[lang] },
    { id: 'tabs', label: TEXT.tabs[lang] },
    { id: 'search', label: TEXT.search[lang] },
    { id: 'filters', label: TEXT.filters[lang] },
    { id: 'inputs', label: TEXT.inputs[lang] },
    { id: 'dateTags', label: TEXT.dateTags[lang] },
    { id: 'loading', label: TEXT.loading[lang] }
  ];

  const sampleTableData = [
    { id: 1, name: '김철수', email: 'kim@example.com', status: 'confirmed', date: '2024-01-15' },
    { id: 2, name: '이영희', email: 'lee@example.com', status: 'pending', date: '2024-01-16' },
    { id: 3, name: '박민수', email: 'park@example.com', status: 'cancelled', date: '2024-01-17' }
  ];

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return TEXT.pending[lang];
      case 'confirmed': return TEXT.confirmed[lang];
      case 'cancelled': return TEXT.cancelled[lang];
      case 'completed': return TEXT.completed[lang];
      case 'deposit_confirmed': return TEXT.depositConfirmed[lang];
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'confirmed': return statusStyles.statusConfirmed;
      case 'pending': return statusStyles.statusPending;
      case 'cancelled': return statusStyles.statusCancelled;
      case 'completed': return statusStyles.statusCompleted;
      case 'deposit_confirmed': return statusStyles.statusDepositConfirmed;
      default: return '';
    }
  };

  const renderPageLayout = () => (
    <div className={styles.section}>
      <h3>스타일 가이드 사용법</h3>
      <div className={styles.textExample}>
        <h2>개발자를 위한 UI 컴포넌트 가이드</h2>
        <p>이 페이지는 GNEGNEGNE 프로젝트의 모든 UI 컴포넌트 스타일을 보여주는 참고 자료입니다.</p>

        <h3>사용 방법</h3>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
          <li><strong>탭 메뉴</strong>: 상단의 탭을 클릭하여 원하는 컴포넌트 카테고리를 선택하세요.</li>
          <li><strong>스타일 복사</strong>: 각 컴포넌트의 스타일 클래스를 확인하고 코드에 적용하세요.</li>
          <li><strong>일관성 유지</strong>: 새로운 페이지를 만들 때 이 가이드를 참고하여 일관된 디자인을 유지하세요.</li>
        </ul>

        <h3>주요 컴포넌트</h3>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
          <li><strong>페이지 레이아웃</strong>: 페이지 여백, 타이틀 스타일</li>
          <li><strong>텍스트</strong>: 제목, 본문, 크기별 텍스트 스타일</li>
          <li><strong>버튼</strong>: 기본 버튼, 검색 폼 버튼</li>
          <li><strong>입력 폼</strong>: 텍스트 입력, 날짜/시간 입력, 텍스트 영역</li>
          <li><strong>체크박스/라디오박스</strong>: 다양한 상태의 입력 요소</li>
          <li><strong>테이블</strong>: 기본 테이블, 로딩 상태, 빈 데이터</li>
          <li><strong>모달</strong>: 기본 모달, 메시지 모달</li>
          <li><strong>상태</strong>: 다양한 상태 표시</li>
          <li><strong>탭</strong>: 탭 네비게이션</li>
          <li><strong>검색/필터</strong>: 검색 폼, 상태 필터</li>
          <li><strong>날짜 태그</strong>: 날짜 태그 컴포넌트</li>
          <li><strong>로딩</strong>: 로딩 상태</li>
        </ul>

        <h3>스타일 유틸리티</h3>
        <p>모든 스타일은 <code>src/utils/styleUtils.js</code>에서 관리되며, 다음과 같이 사용할 수 있습니다:</p>
        <pre style={{
          background: '#f8f9fa',
          padding: '12px',
          borderRadius: '4px',
          fontSize: '12px',
          overflow: 'auto'
        }}>
          {`// 스타일 유틸리티 가져오기
const { getPageContainerStyles, getModalStyles } = require('../utils/styleUtils');

const pageContainerStyles = getPageContainerStyles();
const modalStyles = getModalStyles();`}
        </pre>

        <h3>페이지 여백 스타일</h3>
        <div className={styles.pageMarginExample}>
          <div className={styles.content}>
            <p>이것은 페이지 컨테이너의 여백 스타일입니다. padding: 3px, max-width: 1200px, margin: 0 auto</p>
          </div>
        </div>

        <h3>페이지 타이틀 스타일</h3>
        <div className={styles.textExample}>
          <h1 className={pageContainerStyles.pageTitle}>페이지 타이틀</h1>
          <p>font-size: 1.5rem, font-weight: 300, color: #111, margin: 0 0 16px 0</p>
        </div>
      </div>
    </div>
  );

  const renderText = () => (
    <div className={styles.section}>
      <h3>일반 내용 텍스트 스타일</h3>
      <div className={styles.textStyles}>
        <div className={styles.textExample}>
          <h1>제목 1 (24px, 600)</h1>
          <h2>제목 2 (20px, 500)</h2>
          <h3>제목 3 (18px, 500)</h3>
          <p>일반 텍스트 (14px, 400) - 이것은 일반적인 본문 텍스트입니다. line-height: 1.5로 가독성을 높였습니다.</p>
          <p className={styles.large}>큰 텍스트 (16px, 400)</p>
          <p className={styles.small}>작은 텍스트 (12px, 400)</p>
        </div>
      </div>
    </div>
  );

  const renderButtons = () => (
    <div className={styles.section}>
      <h3>기본 버튼 스타일</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <button className={modalActionStyles.actionButton}>
          {TEXT.primary[lang]}
        </button>
        <button className={modalActionStyles.actionButton}>
          {TEXT.secondary[lang]}
        </button>
        <button className={modalActionStyles.actionButton}>
          {TEXT.delete[lang]}
        </button>
        <button className={modalActionStyles.actionButton}>
          {TEXT.confirm[lang]}
        </button>
      </div>
      <p style={{ fontSize: '16px', color: '#666', marginTop: '12px', lineHeight: '1.6' }}>
        <strong>스타일 클래스:</strong> <code>modalActionStyles.actionButton</code><br />
        <strong>특징:</strong> 흰색 배경, 검은색 테두리, 호버 시 그림자 효과, 대문자 텍스트
      </p>

      <h3>검색 폼 버튼</h3>
      <form className={searchStyles.searchForm}>
        <select>
          <option>{TEXT.name[lang]}</option>
          <option>{TEXT.email[lang]}</option>
        </select>
        <input type="text" placeholder={TEXT.searchPlaceholder[lang]} />
        <button type="submit">{TEXT.search[lang]}</button>
      </form>
      <p style={{ fontSize: '14px', color: '#666', marginTop: '12px' }}>
        <strong>스타일 클래스:</strong> <code>searchStyles.searchForm</code><br />
        <strong>특징:</strong> 검색 폼 전체 스타일, select, input, button이 포함된 복합 컴포넌트
      </p>

      <h3>다양한 버튼 스타일</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <button className={modalActionStyles.secondaryButton}>
          보조 버튼
        </button>
        <button className={modalActionStyles.deleteButton}>
          삭제 버튼
        </button>
        <button className={modalActionStyles.gradientPinkButton}>
          그라데이션 버튼
        </button>
      </div>
      <p style={{ fontSize: '14px', color: '#666', marginTop: '12px' }}>
        <strong>다른 버튼 스타일:</strong> secondaryButton, deleteButton, gradientPinkButton
      </p>
    </div>
  );

  const renderForms = () => (
    <div className={styles.section}>
      <h3>입력 폼 스타일</h3>
      <div className={styles.formGroup}>
        <input
          type="text"
          className={commonInputStyles.commonInput}
          placeholder={TEXT.name[lang]}
        />
        <input
          type="email"
          className={commonInputStyles.commonInput}
          placeholder={TEXT.email[lang]}
        />
        <input
          type="tel"
          className={commonInputStyles.commonInput}
          placeholder={TEXT.phone[lang]}
        />
      </div>

      <h3>날짜/시간 입력</h3>
      <div className={styles.formGroup}>
        <input
          type="datetime-local"
          className={dateTimeInputStyles.dateTimeInput}
        />
      </div>

      <h3>텍스트 영역</h3>
      <div className={styles.formGroup}>
        <textarea
          className={modalContentStyles.modalTextarea}
          placeholder={TEXT.description[lang]}
          rows={4}
        />
        <div className={modalContentStyles.modalTextareaNote}>
          * {TEXT.sampleText[lang]}
        </div>
      </div>
    </div>
  );

  const renderCheckboxes = () => (
    <div className={styles.section}>
      <h3>체크박스 스타일</h3>
      <div className={styles.checkboxGroup}>
        <div className={styles.checkboxItem}>
          <input type="checkbox" id="checkbox1" />
          <label htmlFor="checkbox1">기본 체크박스</label>
        </div>
        <div className={styles.checkboxItem}>
          <input type="checkbox" id="checkbox2" defaultChecked />
          <label htmlFor="checkbox2">체크된 체크박스</label>
        </div>
        <div className={styles.checkboxItem}>
          <input type="checkbox" id="checkbox3" disabled />
          <label htmlFor="checkbox3">비활성화된 체크박스</label>
        </div>
        <div className={styles.checkboxItem}>
          <input type="checkbox" id="checkbox4" disabled defaultChecked />
          <label htmlFor="checkbox4">비활성화된 체크된 체크박스</label>
        </div>
      </div>
    </div>
  );

  const renderRadios = () => (
    <div className={styles.section}>
      <h3>라디오박스 스타일</h3>
      <div className={styles.radioGroup}>
        <div className={styles.radioItem}>
          <input type="radio" name="radioGroup" id="radio1" />
          <label htmlFor="radio1">옵션 1</label>
        </div>
        <div className={styles.radioItem}>
          <input type="radio" name="radioGroup" id="radio2" defaultChecked />
          <label htmlFor="radio2">옵션 2 (선택됨)</label>
        </div>
        <div className={styles.radioItem}>
          <input type="radio" name="radioGroup" id="radio3" />
          <label htmlFor="radio3">옵션 3</label>
        </div>
        <div className={styles.radioItem}>
          <input type="radio" name="radioGroup2" id="radio4" disabled />
          <label htmlFor="radio4">비활성화된 라디오박스</label>
        </div>
      </div>
    </div>
  );

  const renderTables = () => (
    <div className={styles.section}>
      <h3>기본 테이블</h3>
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.tableHeader}>
            <tr>
              <th>{TEXT.name[lang]}</th>
              <th>{TEXT.email[lang]}</th>
              <th>{TEXT.status[lang]}</th>
              <th>{TEXT.date[lang]}</th>
            </tr>
          </thead>
          <tbody className={tableStyles.tableBody}>
            {sampleTableData.map(row => (
              <tr key={row.id} className={tableStyles.tableRow}>
                <td>{row.name}</td>
                <td>{row.email}</td>
                <td className={`${statusStyles.statusCell} ${getStatusClass(row.status)}`}>
                  {getStatusText(row.status)}
                </td>
                <td>{row.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>로딩 상태</h3>
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.tableHeader}>
            <tr>
              <th>{TEXT.name[lang]}</th>
              <th>{TEXT.email[lang]}</th>
            </tr>
          </thead>
          <tbody className={tableStyles.tableBody}>
            <tr>
              <td colSpan={2} className={loadingStyles.loadingCell}>
                {TEXT.loadingText[lang]}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>빈 데이터</h3>
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.tableHeader}>
            <tr>
              <th>{TEXT.name[lang]}</th>
              <th>{TEXT.email[lang]}</th>
            </tr>
          </thead>
          <tbody className={tableStyles.tableBody}>
            <tr>
              <td colSpan={2} className={loadingStyles.emptyCell}>
                {TEXT.noData[lang]}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderModals = () => (
    <div className={styles.section}>
      <h3>모달 예시</h3>
      <div style={{
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '6px',
        fontSize: '14px',
        lineHeight: '1.6'
      }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>모달 구현 가이드</h4>
        <ul style={{ margin: '0', paddingLeft: '20px' }}>
          <li><strong>백그라운드 스크롤 방지:</strong> 모달 열릴 때 <code>document.body.style.overflow = 'hidden'</code> 설정</li>
          <li><strong>모달 오버레이:</strong> <code>modalStyles.modalOverlay</code> 클래스 사용</li>
          <li><strong>모달 컨테이너:</strong> <code>modalStyles.modal</code> 클래스 사용</li>
          <li><strong>헤더:</strong> <code>modalStyles.modalHeader</code> + <code>modalStyles.closeButton</code></li>
          <li><strong>컨텐츠:</strong> <code>modalStyles.modalContent</code> + <code>modalContentStyles.modalInfoSection</code></li>
          <li><strong>액션 버튼:</strong> <code>modalActionStyles.modalActions</code> + <code>modalActionStyles.actionButton</code></li>
          <li><strong>부 컨테이너:</strong> 연한 배경색, 작은 패딩, 둥근 모서리 스타일</li>
        </ul>
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
          <strong>참고:</strong> 모달이 열려있을 때는 백그라운드 스크롤이 자동으로 방지됩니다.
        </div>
      </div>
      <div className={styles.buttonGroup}>
        <button
          className={modalActionStyles.actionButton}
          onClick={() => setShowModal(true)}
        >
          {TEXT.openModal[lang]}
        </button>
        <button
          className={modalActionStyles.secondaryButton}
          onClick={() => setShowMessageModal(true)}
        >
          {TEXT.openMessageModal[lang]}
        </button>
      </div>

      {/* 기본 모달 */}
      {showModal && (
        <div className={modalStyles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={modalStyles.modalHeader}>
              <h2>{TEXT.sampleText[lang]}</h2>
              <button className={modalStyles.closeButton} onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className={modalStyles.modalContent}>
              <div className={modalContentStyles.modalInfoSection}>
                <div className={modalContentStyles.modalInfoRow}>
                  <div className={modalContentStyles.modalInfoItem}>
                    <div className={modalContentStyles.modalInfoLabel}>{TEXT.name[lang]}</div>
                    <div className={modalContentStyles.modalInfoValue}>{TEXT.sampleData[lang]}</div>
                  </div>
                  <div className={modalContentStyles.modalInfoItem}>
                    <div className={modalContentStyles.modalInfoLabel}>{TEXT.email[lang]}</div>
                    <div className={modalContentStyles.modalInfoValue}>{TEXT.sampleData[lang]}</div>
                  </div>
                </div>
              </div>

              {/* 새로운 부 컨테이너 예시 */}
              <div style={{
                marginTop: '7px',
                padding: '5px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '6px'
              }}>
                <h4 style={{
                  margin: '0 0 4px 0',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  추가 정보
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '4px'
                }}>
                  <div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      전화번호
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      010-1234-5678
                    </div>
                  </div>
                  <div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      상태
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#28a745'
                    }}>
                      활성
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={modalActionStyles.modalActions} style={{ width: '100%', paddingLeft: '5px', paddingRight: '5px' }}>
              <div className={modalActionStyles.leftActions} style={{ flex: 1 }}>
                <button className={modalActionStyles.actionButton} style={{ width: '100%', maxWidth: 'none' }}>
                  {TEXT.delete[lang]}
                </button>
              </div>
              <div className={modalActionStyles.rightActions} style={{ flex: 1 }}>
                <button className={modalActionStyles.actionButton} style={{ width: '100%', maxWidth: 'none' }}>
                  {TEXT.save[lang]}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 메시지 모달 */}
      {showMessageModal && (
        <div className={messageModalStyles.messageModalOverlay} onClick={() => setShowMessageModal(false)}>
          <div className={messageModalStyles.messageModal} onClick={(e) => e.stopPropagation()}>
            <h3>{TEXT.sampleText[lang]}</h3>
            <textarea
              className={messageModalStyles.messageInput}
              placeholder={TEXT.description[lang]}
              rows={4}
            />
            <div className={modalActionStyles.modalActions} style={{ width: '100%', justifyContent: 'flex-end', paddingLeft: '5px', paddingRight: '5px' }}>
              <div className={modalActionStyles.rightActions} style={{ flex: 1 }}>
                <button className={modalActionStyles.actionButton} style={{ width: '100%', maxWidth: 'none' }}>
                  {TEXT.save[lang]}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStatus = () => (
    <div className={styles.section}>
      <h3>상태 표시</h3>
      <div className={styles.statusGroup}>
        <span className={`${statusStyles.statusCell} ${statusStyles.statusPending}`}>
          {TEXT.pending[lang]}
        </span>
        <span className={`${statusStyles.statusCell} ${statusStyles.statusConfirmed}`}>
          {TEXT.confirmed[lang]}
        </span>
        <span className={`${statusStyles.statusCell} ${statusStyles.statusCancelled}`}>
          {TEXT.cancelled[lang]}
        </span>
        <span className={`${statusStyles.statusCell} ${statusStyles.statusCompleted}`}>
          {TEXT.completed[lang]}
        </span>
        <span className={`${statusStyles.statusCell} ${statusStyles.statusDepositConfirmed}`}>
          {TEXT.depositConfirmed[lang]}
        </span>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className={styles.section}>
      <h3>탭 스타일 (Calendar.jsx 스타일)</h3>
      <div className={styles.tabContent}>
        <div style={{
          display: 'flex',
          marginBottom: '5px',
          gap: '0',
          justifyContent: 'center',
          position: 'relative'
        }}>
          {/* 왼쪽 직선 */}
          <div style={{
            position: 'absolute',
            left: `-${lineWidth}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            width: `${lineWidth}px`,
            height: '1px',
            backgroundColor: '#000',
            zIndex: '10'
          }} />

          {/* 오른쪽 직선 */}
          <div style={{
            position: 'absolute',
            right: `-${lineWidth}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            width: `${lineWidth}px`,
            height: '1px',
            backgroundColor: '#000',
            zIndex: '10'
          }} />

          {/* 탭 컨테이너 */}
          <div
            style={{
              display: 'flex',
              gap: '0',
              justifyContent: 'center',
              border: '1px solid #000',
              borderRadius: '8px',
              padding: '0'
            }}
          >
            <button
              style={{
                padding: '4px 16px',
                border: '1px solid transparent',
                backgroundColor: '#fff',
                color: '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRight: '1px solid transparent',
                transform: 'scale(1)',
                zIndex: '1'
              }}
            >
              {TEXT.tab1[lang]}
            </button>
            <button
              style={{
                padding: '4px 16px',
                border: '1px solid #000',
                backgroundColor: '#fff',
                color: '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: '1px solid #000',
                borderRight: '1px solid #000',
                transform: 'scale(1.15)',
                zIndex: '2'
              }}
            >
              {TEXT.tab2[lang]}
            </button>
            <button
              style={{
                padding: '4px 16px',
                border: '1px solid transparent',
                backgroundColor: '#fff',
                color: '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: '1px solid transparent',
                borderRight: '1px solid transparent',
                transform: 'scale(1)',
                zIndex: '1'
              }}
            >
              {TEXT.tab3[lang]}
            </button>
            <button
              style={{
                padding: '4px 16px',
                border: '1px solid transparent',
                backgroundColor: '#fff',
                color: '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: '1px solid transparent',
                transform: 'scale(1)',
                zIndex: '1'
              }}
            >
              {TEXT.tab4[lang]}
            </button>
          </div>
        </div>
      </div>
      <p style={{ fontSize: '14px', color: '#666', marginTop: '12px' }}>
        <strong>특징:</strong> 검은색 테두리, 흰색 배경, 선택된 탭은 scale(1.15)로 확대, 좌우 직선 연결, 부드러운 전환 효과
      </p>
    </div>
  );

  const renderSearch = () => (
    <div className={styles.section}>
      <h3>검색 폼</h3>
      <form className={searchStyles.searchForm}>
        <select>
          <option>{TEXT.name[lang]}</option>
          <option>{TEXT.email[lang]}</option>
          <option>{TEXT.status[lang]}</option>
        </select>
        <input type="text" placeholder={TEXT.searchPlaceholder[lang]} />
        <button type="submit">{TEXT.search[lang]}</button>
      </form>
    </div>
  );

  const renderFilters = () => (
    <div className={styles.section}>
      <h3>상태 필터</h3>
      <div className={statusFilterStyles.statusFilter}>
        <button className={`${statusFilterStyles.statusButton} ${statusFilterStyles.selected}`}>
          {TEXT.all[lang]}
        </button>
        <button className={statusFilterStyles.statusButton}>
          {TEXT.pending[lang]}
        </button>
        <button className={statusFilterStyles.statusButton}>
          {TEXT.confirmed[lang]}
        </button>
        <button className={statusFilterStyles.statusButton}>
          {TEXT.cancelled[lang]}
        </button>
      </div>
    </div>
  );

  const renderInputs = () => (
    <div className={styles.section}>
      <h3>입력 필드</h3>
      <div className={styles.formGroup}>
        <input
          type="text"
          className={commonInputStyles.commonInput}
          placeholder={TEXT.name[lang]}
        />
        <input
          type="email"
          className={commonInputStyles.commonInput}
          placeholder={TEXT.email[lang]}
        />
        <input
          type="tel"
          className={commonInputStyles.commonInput}
          placeholder={TEXT.phone[lang]}
        />
      </div>

      <h3>날짜/시간 입력</h3>
      <div className={styles.formGroup}>
        <input
          type="datetime-local"
          className={dateTimeInputStyles.dateTimeInput}
        />
      </div>

      <h3>텍스트 영역</h3>
      <div className={styles.formGroup}>
        <textarea
          className={modalContentStyles.modalTextarea}
          placeholder={TEXT.description[lang]}
          rows={4}
        />
        <div className={modalContentStyles.modalTextareaNote}>
          * {TEXT.sampleText[lang]}
        </div>
      </div>
    </div>
  );

  const renderDateTags = () => (
    <div className={styles.section}>
      <h3>날짜 태그</h3>
      <div className={dateTagStyles.dateTagsContainer}>
        <div className={dateTagStyles.dateTag}>
          오늘
          <span className={dateTagStyles.dateTagCount}>3</span>
        </div>
        <div className={dateTagStyles.dateTag}>
          내일
          <span className={dateTagStyles.dateTagCount}>5</span>
        </div>
        <div className={dateTagStyles.dateTag}>
          이번주
          <span className={dateTagStyles.dateTagCount}>12</span>
        </div>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className={styles.section}>
      <h3>로딩 상태</h3>
      <div className={styles.loadingGroup}>
        <div className={loadingStyles.loadingCell}>
          {TEXT.loadingText[lang]}
        </div>
        <div className={loadingStyles.emptyCell}>
          {TEXT.noData[lang]}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'pageLayout': return renderPageLayout();
      case 'text': return renderText();
      case 'buttons': return renderButtons();
      case 'forms': return renderForms();
      case 'checkboxes': return renderCheckboxes();
      case 'radios': return renderRadios();
      case 'tables': return renderTables();
      case 'modals': return renderModals();
      case 'status': return renderStatus();
      case 'tabs': return renderTabs();
      case 'search': return renderSearch();
      case 'filters': return renderFilters();
      case 'inputs': return renderInputs();
      case 'dateTags': return renderDateTags();
      case 'loading': return renderLoading();
      default: return renderPageLayout();
    }
  };

  return (
    <div className={pageContainerStyles.pageContainer}>
      <h1 className={pageContainerStyles.pageTitle}>{TEXT.pageTitle[lang]}</h1>

      {/* 탭 네비게이션 */}
      <div style={{
        display: 'flex',
        marginBottom: '20px',
        gap: '0',
        justifyContent: 'center',
        position: 'relative'
      }}>
        {/* 왼쪽 직선 */}
        <div style={{
          position: 'absolute',
          left: `-${lineWidth}px`,
          top: '50%',
          transform: 'translateY(-50%)',
          width: `${lineWidth}px`,
          height: '1px',
          backgroundColor: '#000',
          zIndex: '10'
        }} />

        {/* 오른쪽 직선 */}
        <div style={{
          position: 'absolute',
          right: `-${lineWidth}px`,
          top: '50%',
          transform: 'translateY(-50%)',
          width: `${lineWidth}px`,
          height: '1px',
          backgroundColor: '#000',
          zIndex: '10'
        }} />

        {/* 탭 컨테이너 */}
        <div
          className="styleguide-tab-container"
          style={{
            display: 'flex',
            gap: '0',
            justifyContent: 'center',
            border: '1px solid #000',
            borderRadius: '8px',
            padding: '0',
            flexWrap: 'wrap',
            maxWidth: '90vw',
            minWidth: 'fit-content'
          }}
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '4px 12px',
                border: activeTab === tab.id ? '1px solid #000' : '1px solid transparent',
                backgroundColor: '#fff',
                color: '#000',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '400',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: activeTab === tab.id ? '1px solid #000' : '1px solid transparent',
                borderRight: activeTab === tab.id ? '1px solid #000' : '1px solid transparent',
                transform: activeTab === tab.id ? 'scale(1.15)' : 'scale(1)',
                zIndex: activeTab === tab.id ? '2' : '1',
                // 첫 번째 탭
                ...(index === 0 && {
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px'
                }),
                // 마지막 탭
                ...(index === tabs.length - 1 && {
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px'
                })
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 컨텐츠 */}
      <div className={styles.content}>
        {renderContent()}
      </div>
    </div>
  );
};

export default StyleGuidePage;
